// ============================================================
// Construction PURE du document « HORAIRES TYPES » — AUCUNE dépendance Angular.
// Testé sans TestBed : hr-schedule-print.spec.ts.
// ============================================================
//
// CE QU'IL IMPRIME — la grille THÉORIQUE d'un service (hr_schedule + ses créneaux), et non une
// semaine réelle : c'est le document qu'on affiche dans l'entreprise et qu'on remet à l'embauche
// (« voici tes horaires »). Le planning de la semaine, lui, sort de hr-planning.component.
//
// LE ROULEMENT EST LE SUJET — une grille à cycle de 2 semaines n'est PAS imprimable en une seule
// table : ce sont deux grilles, et le papier doit dire laquelle s'applique quand. D'où deux
// choses que l'écran n'a pas besoin de porter :
//   — une section par semaine du cycle, titrée « Semaine 1/2 », sous-titrée des semaines réelles
//     concernées (« S35, S37, S39… ») ;
//   — un CALENDRIER DU ROULEMENT en fin de document, qui déroule les semaines à venir et dit,
//     pour chacune, quelle grille s'applique. Sans lui, un salarié devant le panneau ne peut pas
//     savoir si « cette semaine » est la 1 ou la 2 — la question qui revient à chaque affichage.
//
// L'ancrage se calcule TOUJOURS par weekIndexOf (modulo ancré sur `anchor_monday`), jamais par la
// parité du numéro de semaine ISO : une année à 53 semaines décalerait tout le roulement.

import { PrintDay, PrintDoc, PrintFoot, PrintRotation, PrintRow, PrintSection, printHours } from './hr-print';
import { addDays, isoWeekOf, minutesLabel, mondayOf, plannedMinutes, schedulePeriodLabel, weekIndexOf } from './hr-schedule-engine';

const DAY_LABELS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

// Créneau théorique du cycle — structure de HrSlot, sans importer le modèle (qui traîne
// @angular/forms derrière lui et rendrait ce module impur).
export interface SchedulePrintSlot {
	id_employee: number;
	week_index: number;						// 0..cycle_weeks-1
	weekday: number;							// 1..7 ISO (1 = lundi)
	start_min: number;
	end_min: number;
	break_min: number;
}

export interface SchedulePrintMember {
	id: number;
	name: string;
	color: string;
	contract_min: number | null;					// volume contractuel hebdo en minutes ; null = non renseigné
	cycle_contract_min?: number[] | null;	// MODULATION : volume par semaine du cycle (34/36 h…)
	counts_hours?: boolean;								// false (forfait jours, gérant) ⇒ pas de total affiché
}

// UNE grille à imprimer, ses libellés déjà résolus (le module reste pur : il ne sait pas lire
// un magasin ni un service).
export interface SchedulePrintGrid {
	name: string;
	storeName: string;
	activityName: string;
	cycle_weeks: number;
	anchor_monday: string;								// lundi de la semaine d'indice 0
	datstart: string | null;
	datend: string | null;
	is_active?: boolean;
	slots: SchedulePrintSlot[];
	members: SchedulePrintMember[];				// DÉJÀ dans l'ordre d'affichage (roster)
	openHours?: string[];									// 7 entrées « 09:00 – 19:00 » ; '' si le service n'ouvre pas
}

export interface SchedulePrintInput {
	storeName: string;										// « Pdv Ollioules » ou « Tous les magasins »
	printedAt: string;										// « 26/07/2026 »
	today: string;												// « YYYY-MM-DD » — repère « semaine en cours »
	grids: SchedulePrintGrid[];
	rotationWeeks?: number;								// horizon du calendrier de roulement (défaut 12)
}

const fr = (dat: string) => dat.slice(8, 10) + '/' + dat.slice(5, 7);
const frLong = (dat: string) => dat.slice(8, 10) + '/' + dat.slice(5, 7) + '/' + dat.slice(0, 4);

// La grille s'applique-t-elle ce lundi-là ? (fenêtre de validité, bornes incluses)
function appliesOnWeek(grid: SchedulePrintGrid, monday: string): boolean {
	const sunday = addDays(monday, 6);
	if (grid.datstart && sunday < grid.datstart) return false;
	if (grid.datend && monday > grid.datend) return false;
	return true;
}

// Le premier lundi à partir duquel on déroule le roulement : aujourd'hui si la grille est déjà en
// vigueur, sinon son premier jour d'application — imprimer les horaires d'une grille qui commence
// en septembre doit montrer SEPTEMBRE, pas les semaines d'ici là où elle ne s'applique pas.
function rotationStart(grid: SchedulePrintGrid, today: string): string {
	const from = grid.datstart && grid.datstart > today ? grid.datstart : today;
	return mondayOf(from);
}

// Les semaines réelles À VENIR que porte cette semaine du cycle : « S35, S37, S39 ».
function realWeeksFor(grid: SchedulePrintGrid, weekIndex: number, today: string, horizon: number): string[] {
	if (grid.cycle_weeks <= 1) return [];
	const out: string[] = [];
	let monday = rotationStart(grid, today);
	for (let i = 0; i < horizon; i++) {
		if (appliesOnWeek(grid, monday) && weekIndexOf(monday, grid.anchor_monday, grid.cycle_weeks) === weekIndex) {
			out.push('S' + isoWeekOf(monday));
		}
		monday = addDays(monday, 7);
	}
	return out;
}

// Les jours à imprimer : ceux où le service ouvre OU porte au moins un créneau, toutes semaines du
// cycle confondues. Une colonne « Dimanche » vide sur toutes les grilles n'apprend rien et vole la
// largeur des autres. Communs à TOUTES les semaines du cycle, sinon les grilles ne s'alignent plus.
function visibleWeekdays(grid: SchedulePrintGrid): number[] {
	const all = [1, 2, 3, 4, 5, 6, 7];
	const kept = all.filter(wd =>
		!!(grid.openHours?.[wd - 1] ?? '') || grid.slots.some(s => s.weekday === wd));
	return kept.length > 0 ? kept : all;
}

function contractMinOf(member: SchedulePrintMember, weekIndex: number): number | null {
	const cycle = member.cycle_contract_min;
	if (cycle && cycle.length > 0) return cycle[weekIndex % cycle.length] ?? member.contract_min;
	return member.contract_min;
}

function buildRows(grid: SchedulePrintGrid, weekIndex: number, weekdays: number[]): PrintRow[] {
	const weekSlots = grid.slots.filter(s => s.week_index === weekIndex);
	// Un créneau porté par quelqu'un qui n'est plus dans le roster ne doit pas disparaître du
	// papier : il est bien planifié. On l'ajoute en fin de liste plutôt que de le taire.
	const extra = Array.from(new Set(weekSlots.map(s => s.id_employee)))
		.filter(id => !grid.members.some(m => m.id === id))
		.map(id => ({ id, name: 'Employé #' + id, color: '', contract_min: null } as SchedulePrintMember));

	return [...grid.members, ...extra].map(member => {
		const mine = weekSlots.filter(s => s.id_employee === member.id);
		const total = plannedMinutes(mine);
		const contract = contractMinOf(member, weekIndex);
		const counts = member.counts_hours !== false;
		return {
			name: member.name,
			subtitle: contract != null ? 'contrat ' + printHours(contract) : '',
			color: member.color ?? '',
			chips: [],
			total_min: total,
			delta_min: contract == null || !counts ? null : total - contract,
			counts_hours: counts,
			cells: weekdays.map(wd => ({
				dat: '',
				absence: null,
				pending: false,
				sub: '',
				holiday: false,
				closed: !(grid.openHours?.[wd - 1] ?? '') && !grid.slots.some(s => s.weekday === wd),
				blocks: mine
					.filter(s => s.weekday === wd)
					.sort((a, b) => a.start_min - b.start_min)
					.map(s => ({
						who: '',
						place: grid.activityName,
						from: minutesLabel(s.start_min),
						to: minutesLabel(s.end_min),
						break_min: s.break_min ?? 0,
						color: member.color ?? '',
					})),
			})),
		};
	});
}

function buildFoot(grid: SchedulePrintGrid, weekIndex: number, weekdays: number[]): PrintFoot[] {
	return weekdays.map(wd => {
		const day = grid.slots.filter(s => s.week_index === weekIndex && s.weekday === wd);
		return {
			people: new Set(day.map(s => s.id_employee)).size + ' pers.',
			hours: printHours(plannedMinutes(day)),
			closed: day.length === 0,
		};
	});
}

function buildDays(grid: SchedulePrintGrid, weekdays: number[]): PrintDay[] {
	return weekdays.map(wd => ({
		dat: '',
		label: DAY_LABELS[wd - 1],
		hours: grid.openHours?.[wd - 1] ?? '',
		holiday: null,
		closed: !(grid.openHours?.[wd - 1] ?? '') && !grid.slots.some(s => s.weekday === wd),
	}));
}

// Le calendrier du roulement : ce que le papier ne peut pas déduire tout seul.
function buildRotation(grid: SchedulePrintGrid, today: string, horizon: number): PrintRotation | null {
	if (grid.cycle_weeks <= 1) return null;
	const weeks: PrintRotation['weeks'] = [];
	const thisMonday = mondayOf(today);
	let monday = rotationStart(grid, today);
	for (let i = 0; i < horizon; i++) {
		if (appliesOnWeek(grid, monday)) {
			const wi = weekIndexOf(monday, grid.anchor_monday, grid.cycle_weeks);
			weeks.push({
				label: 'S' + isoWeekOf(monday),
				range: 'du ' + fr(monday) + ' au ' + fr(addDays(monday, 6)),
				cycle: 'Semaine ' + (wi + 1) + '/' + grid.cycle_weeks,
				current: monday === thisMonday,
			});
		}
		monday = addDays(monday, 7);
	}
	return weeks.length ? { title: 'Calendrier du roulement', weeks } : null;
}

/// Assemble le document imprimable d'une ou plusieurs grilles d'horaires types.
/// Une section par semaine du cycle et par grille ; chaque grille commence sur une page neuve.
export function buildSchedulePrintDoc(input: SchedulePrintInput): PrintDoc {
	const horizon = input.rotationWeeks ?? 12;
	const sections: PrintSection[] = [];
	const many = input.grids.length > 1;

	input.grids.forEach((grid, gi) => {
		const weekdays = visibleWeekdays(grid);
		const days = buildDays(grid, weekdays);
		const cycle = Math.max(1, grid.cycle_weeks);
		const period = schedulePeriodLabel(grid);

		for (let wi = 0; wi < cycle; wi++) {
			const rows = buildRows(grid, wi, weekdays);
			const foot = buildFoot(grid, wi, weekdays);
			const realWeeks = realWeeksFor(grid, wi, input.today, horizon);
			const gridName = [grid.storeName, grid.activityName].filter(Boolean).join(' — ') || grid.name;
			const weekTitle = cycle > 1 ? 'Semaine ' + (wi + 1) + '/' + cycle : '';

			sections.push({
				title: many ? [gridName, weekTitle].filter(Boolean).join(' · ') : weekTitle,
				subtitle: [
					realWeeks.length ? "s'applique aux semaines " + realWeeks.join(', ') : '',
					many ? period : '',
				].filter(Boolean).join(' · '),
				caption: 'Horaires types — ' + gridName + (period ? ' — ' + period : ''),
				rowHeader: 'Salarié',
				totalHeader: 'Total',
				footHeader: 'Présence',
				footTotal: printHours(rows.reduce((sum, r) => sum + r.total_min, 0)),
				days,
				rows,
				foot,
				timesheet: false,
				// Chaque grille sur sa page : deux services distincts ne se lisent pas à la suite,
				// et un affichage en salle de repos se découpe service par service.
				pageBreak: many && gi > 0 && wi === 0,
			});
		}
	});

	const only = input.grids.length === 1 ? input.grids[0] : null;
	const cycleLabel = only
		? (only.cycle_weeks > 1
			? 'roulement ' + only.cycle_weeks + ' semaines' + (only.cycle_weeks === 2 ? ' (alternance)' : '')
			: 'semaine fixe')
		: input.grids.length + ' grilles';

	return {
		kind: 'schedule',
		heading: 'Horaires types',
		storeName: input.storeName,
		subtitle: only ? [only.activityName, cycleLabel].filter(Boolean).join(' — ') : cycleLabel,
		periodLabel: only ? schedulePeriodLabel(only) : 'Au ' + frLong(input.today),
		printedAt: input.printedAt,
		legend: '',
		sections,
		ledgers: [],
		notes: [],
		rotation: only ? buildRotation(only, input.today, horizon) : null,
		signature: false,
	};
}
